import React from 'react';
import { useSolubilityStore, TRIAL_PRESETS, TrialId } from '../store/useSolubilityStore';
import { Droplets, Scale, Beaker, Flame, Pause, Thermometer, RotateCcw, Save } from 'lucide-react';

export function SolubilityProcedure() {
  const labStage = useSolubilityStore(s => s.labStage);
  const currentTrial = useSolubilityStore(s => s.currentTrial);
  const waterVolumeMl = useSolubilityStore(s => s.waterVolumeMl);
  const saltOnScaleG = useSolubilityStore(s => s.saltOnScaleG);
  const saltInBeakerG = useSolubilityStore(s => s.saltInBeakerG);
  const stirSeconds = useSolubilityStore(s => s.stirSeconds);
  const isHeating = useSolubilityStore(s => s.isHeating);
  const isStirring = useSolubilityStore(s => s.isStirring);
  const waterTempC = useSolubilityStore(s => s.waterTempC);
  const results = useSolubilityStore(s => s.results);

  const selectTrial = useSolubilityStore(s => s.selectTrial);
  const pourWater = useSolubilityStore(s => s.pourWater);
  const weighSaltStep = useSolubilityStore(s => s.weighSaltStep);
  const addSaltToBeaker = useSolubilityStore(s => s.addSaltToBeaker);
  const toggleHeating = useSolubilityStore(s => s.toggleHeating);
  const setStirring = useSolubilityStore(s => s.setStirring);
  const recordObservation = useSolubilityStore(s => s.recordObservation);
  const resetTrial = useSolubilityStore(s => s.resetTrial);

  const preset = TRIAL_PRESETS[currentTrial];
  const dissolvedFraction = useSolubilityStore(s => s.dissolvedFraction);
  const isPouringWater = useSolubilityStore(s => s.isPouringWater);
  const isTransferringSalt = useSolubilityStore(s => s.isTransferringSalt);
  const stirReady = stirSeconds >= 120;
  const dissolvedG = saltInBeakerG * dissolvedFraction;
  const undissolvedG = saltInBeakerG - dissolvedG;
  const animating = isPouringWater || isTransferringSalt;

  return (
    <div className="flex flex-col h-full pointer-events-auto">
      <div className="pb-4 mb-4 border-b border-border/50">
        <h2 className="text-xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent flex items-center gap-2">
          Salt Solubility vs Temperature
        </h2>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
          <Thermometer className="w-3 h-3" /> Trial {currentTrial}/3 — target {preset.targetTempC}°C
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
        <div className="mb-4">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Select Trial</h3>
          <div className="grid grid-cols-3 gap-2">
            {([1, 2, 3] as TrialId[]).map(id => {
              const done = results.some(r => r.trialId === id);
              const active = currentTrial === id;
              return (
                <button
                  key={id}
                  onClick={() => selectTrial(id)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-all ${
                    active ? 'bg-blue-500 text-white border-blue-500' :
                    done ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' :
                    'bg-card text-foreground border-border hover:border-blue-500/40'
                  }`}
                >
                  {TRIAL_PRESETS[id].targetTempC}°C{done ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-card border border-border space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Stage</span><span className="font-semibold capitalize">{labStage.replace('-', ' ')}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Water</span><span className="font-mono">{waterVolumeMl} mL @ {waterTempC.toFixed(1)}°C</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Salt on scale</span><span className="font-mono">{saltOnScaleG.toFixed(1)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Salt dissolved</span><span className="font-mono text-emerald-500">{dissolvedG.toFixed(1)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Salt undissolved</span><span className="font-mono text-amber-500">{undissolvedG.toFixed(1)} g</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Stirred</span><span className="font-mono">{Math.floor(stirSeconds)}s / 120s</span></div>
        </div>

        <div className="space-y-3">
          <button
            disabled={labStage !== 'setup' || animating}
            onClick={pourWater}
            className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-cyan-500/20"
          >
            <Droplets className="w-4 h-4" /> {isPouringWater ? 'Pouring water…' : `1. Pour 50 mL Water (at ${preset.startTempC}°C)`}
          </button>

          <button
            disabled={(labStage !== 'water-poured' && labStage !== 'salt-weighed') || animating}
            onClick={weighSaltStep}
            className="w-full py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-violet-500/20"
          >
            <Scale className="w-4 h-4" /> 2. Add Salt to Scale (+5 g per click, target 40 g)
          </button>

          <button
            disabled={labStage !== 'salt-weighed' || animating}
            onClick={addSaltToBeaker}
            className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-amber-500/20"
          >
            <Beaker className="w-4 h-4" /> {isTransferringSalt ? 'Transferring salt…' : '3. Transfer Salt to Beaker'}
          </button>

          {preset.needsHeating && (labStage === 'heating' || labStage === 'stirring') && (
            <button
              onClick={toggleHeating}
              className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                isHeating
                  ? 'bg-red-500/10 text-red-500 border border-red-500/50'
                  : 'bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20'
              }`}
            >
              {isHeating ? <><Pause className="w-4 h-4"/> Stop Bunsen</> : <><Flame className="w-4 h-4"/> 4. Heat to {preset.targetTempC}°C</>}
            </button>
          )}

          <button
            disabled={labStage !== 'heating' && labStage !== 'stirring'}
            onClick={() => setStirring(!isStirring)}
            className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isStirring
                ? 'bg-blue-500 text-white shadow-md shadow-blue-500/20'
                : 'bg-muted hover:bg-muted/80 text-foreground border border-border'
            }`}
          >
            <Droplets className="w-4 h-4" /> {isStirring ? `Stirring... ${Math.floor(stirSeconds)}s` : preset.needsHeating ? '5. Stir for 2 min' : '4. Stir for 2 min'}
          </button>

          <button
            disabled={!stirReady || labStage === 'observed' || labStage === 'completed'}
            onClick={recordObservation}
            className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-emerald-500/20"
          >
            <Save className="w-4 h-4" /> Record Observation
          </button>

          <button
            onClick={resetTrial}
            className="w-full py-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-3 h-3" /> Reset Trial
          </button>
        </div>

        <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase tracking-wider mb-2">Tips</h4>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside ml-2">
            <li>Trial 1: water is poured cold (10°C), no Bunsen needed.</li>
            <li>Trials 2 &amp; 3: heat the water to the target before stirring.</li>
            <li>Stir for the full 2 minutes for an accurate reading.</li>
            <li>Observe how much salt remains undissolved at each temperature.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SolubilityProcedure;
